import { Radio, RadioGroup, Stack } from "@chakra-ui/react";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { NotificationManager } from "react-notifications";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import dollor from "../../../assets/icons/dollar.svg";
import percentage from "../../../assets/icons/percentage.svg";
import SearchDropdown from "../../../components/Dropdowns/SearchDropdown";
import InputWithIcon from "../../../components/InputWithIcon";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";

const AddLeadData = ({ showModal, formData, disable, error, onSetFormData, onSetError, handleSubmit, handleClose, from }) => {
  const [config] = useAuth();
  const navigate = useNavigate();
  const selectRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [bdsOptions, setBdsOptions] = useState([]);
  const [linkOptions, setLinkOptions] = useState([]);
  const [brokerOptions, setBrokerOptions] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);
  const [leadSourceOptions, setLeadSourceOptions] = useState([]);
  const statusOptions = [
    { value: 2, label: "Cold" },
    { value: 3, label: "Warm" },
    { value: 1, label: "Hot" },
  ];

  const onSelectClear = () => {
    selectRef.current.clearValue();
  };

  const handleChange = (value, name) => {
    onSetError({ ...error, [name]: "" });
    onSetFormData({ ...formData, [name]: value });
  };

  const handleLeadTypeChange = (value) => {
    onSetError({ ...error, lead_type: "" });
    onSetFormData({ ...formData, lead_type: value, link: "" });
  };

  const handlePortfolioChange = (value) => {
    const portfolioStatus = formData.portfolio ? 0 : 1;
    onSetFormData({ ...formData, portfolio: portfolioStatus, link: "" });
  };

  const fetchUsers = () => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/get-datalisting?list=users`, config)
      .then((res) => {
        const data = res?.data?.data;
        const options = data?.map((el) => ({ value: el?.id, label: `${el?.first_name} ${el?.last_name}`, role: el?.role_id }));
        setBdsOptions(options?.filter((el) => el.role === 3));
        setBrokerOptions(options?.filter((el) => el.role === 3));
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const fetchLeadSources = () => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/get-datalisting?list=lead_sources`, config)
      .then((res) => {
        const data = res?.data?.data;
        const options = data?.lead_sources?.map((el) => ({ value: el?.id, label: el.name }));
        setLeadSourceOptions(options);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const fetchLinks = (inputValue = "") => {
    setLoading(true);

    // Build full API URL with optional search param
    let url = `${BASE_URL}/get-ContactData/${formData?.contact}`;
    if (inputValue.trim() !== "") {
      url += `?search=${encodeURIComponent(inputValue.trim())}`;
    }

    axios
      .get(url, config)
      .then((res) => {
        const data = res?.data?.data;
        const properties = data?.properties;
        const acquisitions = data?.acquisitions;
        const portfolios = data?.portfolios;

        const propertyOptions = properties?.map((el) => ({
          value: el?.id,
          label: el?.property_name,
          desc: `${el.street_address}, ${el.city}, ${el.state}`,
        }));

        const acquisitionOptions = acquisitions?.map((el) => ({
          value: el?.id,
          label: el?.property_type?.[0]?.type,
          minPrice: el?.min_price,
          maxPrice: el?.max_price,
        }));

        const portfolioOptions = portfolios?.map((el) => ({
          value: el?.id,
          label: el?.name,
          desc: ``,
        }));

        if (formData.lead_type === "acquisition" && formData.portfolio !== 1) {
          setLinkOptions(acquisitionOptions);
        } else if (formData.lead_type === "disposition" && formData.portfolio !== 1) {
          setLinkOptions(propertyOptions);
        } else {
          setLinkOptions(portfolioOptions);
        }
      })
      .catch((err) => {
        if (
          err.response?.data?.message &&
          err.response?.data?.message !== "Too Many Attempts."
        ) {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };



  useEffect(() => {
    if (showModal) {
      if (formData?.contact !== "") {
        fetchLinks();
      }
    }
  }, [showModal, formData?.contact, formData?.lead_type, formData?.portfolio]);

  useEffect(() => {
    if (showModal) {
      fetchUsers();
      fetchLeadSources();
    }
  }, [showModal]);

  const fetchSuggestions = (inputValue) => {
    if (inputValue !== "") {
      axios
        .get(`${BASE_URL}/get-datalisting`, {
          ...config,
          params: {
            list: "contacts",
            search: inputValue.trim(), // ✅ axios encodes it
          },
        })
        .then((res) => {
          const data = res?.data?.data;

          const contactOptions = data?.contact_list?.map((el) => ({
            value: el?.id,
            label: `${el?.first_name} ${el?.last_name}`,
          }));

          setContactOptions(contactOptions);
        })
        .catch((err) => {
          if (
            err.response?.data?.message &&
            err.response?.data?.message !== "Too Many Attempts."
          ) {
            NotificationManager.error(err.response?.data?.message);
          }
        });
    } else {
      setContactOptions([]);
    }
  };

  const formatOptionLabel = ({ value, label, minPrice, maxPrice, desc }) => (
    <div>
      <div>{label}</div>
      {desc && <p className="dark-M">{desc}</p>}
      {(minPrice || maxPrice) && (
        <div className="flex gap-2 dark-M">
          {minPrice && <div>Min Price-{minPrice}</div>}/{maxPrice && <div>Max Price-{maxPrice}</div>}
        </div>
      )}
    </div>
  );

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div>
          <label className="dark-H head-4 mb-2 required:*:">
            BDS
          </label>
          <Select
            ref={selectRef}
            className="body-N"
            options={[{ label: "None", value: null }, ...bdsOptions]} // Added blank option
            placeholder="Select"
            value={bdsOptions.find((option) => option?.value === formData.bds) || null}
            onChange={(option) => handleChange(option?.value || null, "bds")} // Allow clearing
          />
        </div>


        <div className="mt-6">
          <label className="dark-H head-4 mb-2 required:*:">
            Broker
          </label>
          <Select
            ref={selectRef}
            className="body-N"
            options={[{ label: "None", value: null }, ...brokerOptions]}
            placeholder="Select"
            isMulti
            value={brokerOptions?.filter((option) =>
              (formData.broker || []).includes(option.value)
            )}
            onChange={(selectedOptions) => {
              handleChange(
                selectedOptions ? selectedOptions.map((opt) => opt.value) : [],
                "broker"
              )
            }}
          />

          {error?.broker && <span className="body-S red-D">{error?.broker}</span>}
        </div>


        <div className="mt-6">
          <label className="dark-H head-4 mb-2 required:*:">
            Contact<span className="red-D">*</span>
          </label>
          <div className="flex-1">
            <SearchDropdown
              isTop={false}
              placeholder="Search Here..."
              text={formData?.contact_name}
              options={contactOptions}
              fetchSuggestions={fetchSuggestions}
              onSetOptions={(value) => setContactOptions(value)}
              handleSelect={(option) => {
                onSetError({ ...error, contact_id: "" });
                onSetFormData({ ...formData, contact_name: option?.label, contact: option?.value });
                setContactOptions([]);
              }}
            />
          </div>
          {error?.contact_id && <span className="body-S red-D">{error?.contact_id}</span>}
        </div>

        {formData?.contact !== "" && (
          <>
            <div className="mt-6">
              <label className="dark-H head-4 required:*:">
                Lead Type<span className="red-D">*</span>
              </label>
              <div className="mt-2">
                <RadioGroup
                  onChange={(value) => {
                    handleLeadTypeChange(value);
                  }}
                  value={formData?.lead_type}
                >
                  <Stack direction="row" gap={5}>
                    <Radio value="disposition">Disposition</Radio>
                    <Radio value="acquisition">Acquisition</Radio>
                  </Stack>
                </RadioGroup>
                {error?.lead_type && <span className="body-S red-D">{error?.lead_type}</span>}
              </div>
            </div>


            <div className="mt-6">
              <label className="container" style={{ marginBottom: "0px" }}>
                <p style={{ paddingLeft: "30px" }} className="dark-H head-4">Portfolio</p>
                <input
                  type="checkbox"
                  checked={formData?.portfolio}
                  className="body-N"
                  onChange={(value) => {
                    handlePortfolioChange(value);
                  }}
                />
                <span className="checkmark"></span>
              </label>
            </div>

            {formData?.lead_type !== "" && (
              <div className="mt-6">
                <label className="dark-H head-4 mb-2 required:*:">
                  Property /Acquisition Criteria<span className="red-D">*</span>
                </label>
                <div className="mt-1.5">
                  <Select
                    className="body-N"
                    options={linkOptions}
                    placeholder="Select"
                    value={linkOptions.find((option) => option.value === formData.link) || null}
                    onChange={(option) => {
                      onSetError({ ...error, link_id: "" });
                      handleChange(option ? option.value : null, "link");
                    }}
                    formatOptionLabel={formatOptionLabel}
                    isSearchable={true}
                    isClearable={true}
                    loading={loading}
                  />
                </div>

                {linkOptions?.length === 0 && (
                  <p className="body-S red-D">
                    No {formData?.lead_type === "disposition" ? "property" : "acquisition criteria"} found under this contact.{" "}
                    <span role="button" className="underline green-H" onClick={() => navigate(`/contact/${formData?.contact}`)}>
                      Click here to add
                    </span>
                  </p>
                )}
                {error?.link_id && <span className="body-S red-D">{error?.link_id}</span>}
              </div>
            )}
          </>
        )}

        <div className="mt-6 ">
          <label className="dark-H head-4 mb-2">
            {`${from} date`}</label>
          <DatePicker
            className="body-N"
            dateFormat="MM/dd/yyyy"
            placeholderText="MM/DD/YYYY"
            selected={formData.created_at ? new Date(formData.created_at) : null}
            onChange={(value) => {
              onSetError({ ...error, created_at: "" });
              handleChange(value, "created_at");
            }}
            maxDate={new Date()}
          />
          {error?.created_at && <p className="body-S red-D">{error?.created_at}</p>}
        </div>

        <div className="mt-6">
          <label className="dark-H head-4 mb-2 required:*:">
            Lead Source<span className="red-D">*</span>
          </label>
          <div className="mt-1.5">
            <Select
              className="body-N"
              options={leadSourceOptions}
              placeholder={"Select"}
              value={leadSourceOptions?.find((option) => option.value === formData.lead_source) || null}
              onChange={(option) => {
                onSetError({ ...error, lead_source_id: "" });
                handleChange(option.value, "lead_source");
              }}
            />
          </div>
          {error?.lead_source_id && <span className="body-S red-D">{error?.lead_source_id}</span>}
        </div>


        {/* 
        <div className="mt-6">
          <label className="dark-H head-4 mb-2 required:*:">
            Lead Status<span className="red-D">*</span>
          </label>
          <div className="mt-1.5">
            <Select
              options={statusOptions}
              value={statusOptions.find((option) => option.value === formData.lead_status) || null}
              onChange={(option) => {
                onSetError({ ...error, lead_status: "" });
                handleChange(option.value, "lead_status");
              }}
              placeholder="Select Status"
              className="body-N"
            />
          </div>
          {error?.link_id && <span className="body-S red-D">{error?.link_id}</span>}
        </div> */}


        {(from === "BOV") && (

          <>
            <div className="mt-6">
              <label className="dark-H head-4 mb-2">Suggested Sale Price</label>
              <InputWithIcon
                icon={dollor}
                className="body-N"
                name="suggested_sale_price"
                type="number"
                placeholder="Write Suggested Sale Price"
                value={formData.suggested_sale_price ?? ""}
                onChange={(e) => handleChange(e.target.value, "suggested_sale_price")}
              />
              {error?.suggested_sale_price && <p className="body-S red-D pt-1">{error?.suggested_sale_price}</p>}
            </div>

            <div className="mt-6">
              <label className="dark-H head-4 mb-2">Suggested Cap Rate</label>
              <InputWithIcon
                icon={percentage}
                className="body-N"
                name="suggested_cap_rate"
                type="number"
                placeholder="Write Suggested Sale Price"
                value={formData.suggested_cap_rate ?? ""}
                onChange={(e) => handleChange(e.target.value, "suggested_cap_rate")}
              />
              {error?.suggested_cap_rate && <p className="body-S red-D pt-1">{error?.suggested_cap_rate}</p>}
            </div>
          </>
        )}

        {/* New Override Reason Field */}
        {/* <div className="mt-6">
          <label className="dark-H head-4 mb-2">
            Override Reason</label>
          <input
            className="body-N"
            name="override_reason"
            type="text"
            placeholder="Add suggestion"
            value={formData.override_reason ?? ""}
            onChange={(e) => handleChange(e.target.value, "override_reason")}
          />
          {error?.override_reason && <p className="body-S red-D pt-1">{error?.override_reason}</p>}
        </div> */}

        <div className="mt-6">
          <button type="submit" disabled={disable} className="save-button light-L head-5 green-bg-H">
            Save
          </button>
          <button
            type="button"
            onClick={() => {
              onSelectClear();
              handleClose();
            }}
            className="green-H ml-5"
          >
            Cancel
          </button>
        </div>
      </form>

    </>
  );
};

export default AddLeadData;